import { useState, type FormEvent } from 'react';
import { Field } from '../ui/Field';
import { btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { ADMIN_STEP_MINUTES, DEFAULT_STAFF_ID, conflictsFor } from '../../core/rules/agenda';
import { useRequestId } from '../../hooks/useRequestId';
import { plural } from '../../core/format/text';
import { blockDurations, blockReasons, durationLabel } from './agendaRules';
import type { AgendaAppointment, AgendaBlock, AgendaBlockRequest, BlockReason } from '../../core/types/agenda';
type Props = {
  initialDate: string;
  initialTime: string;
  appointments: AgendaAppointment[];
  onSubmit: (req: AgendaBlockRequest) => Promise<AgendaBlock | null>;
  onDone: () => void;
  onCancel: () => void;
};

/** Tijd blokkeren: dan kan niemand die tijd boeken, ook niet via de webshop. */
export function BlockForm({
  initialDate,
  initialTime,
  appointments,
  onSubmit,
  onDone,
  onCancel
}: Props) {
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [durationMinutes, setDuration] = useState(60);
  const [reason, setReason] = useState<BlockReason>('privé');
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const requestId = useRequestId(JSON.stringify([date, time, durationMinutes, reason, label.trim()]));

  // Blokkeren over een bestaande afspraak heen mag (ze blijft staan), maar zeg het wel.
  const overlapping = time ? conflictsFor({
    date,
    time,
    durationMinutes,
    staffId: DEFAULT_STAFF_ID
  }, appointments, [], {
    buffer: 0
  }).length : 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date || !time || submitting) return;
    setSubmitting(true);
    const b = await onSubmit({
      requestId,
      staffId: DEFAULT_STAFF_ID,
      date,
      time,
      durationMinutes,
      reason,
      ...(label.trim() ? {
        label: label.trim()
      } : {})
    });
    setSubmitting(false);
    if (b) onDone();
  };
  return <form onSubmit={submit} className="space-y-5" data-id="element-341" data-mp="3ea0fexz2zqnj">
      <div data-id="element-342" data-mp="3e74i9bv25605">
        <h2 className="font-display text-[1.4rem] font-light text-ink" data-id="element-343" data-mp="2bvt3fuui3x2z">Tijd blokkeren</h2>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-344" data-mp="2btzto6olcwqr">Deze tijd verdwijnt meteen uit de vrije tijden op de website.</p>
      </div>

      <div role="radiogroup" aria-label="Reden" className="flex flex-wrap gap-1.5" data-id="element-345" data-mp="2kvzdt7uwkgxx">
        {blockReasons.map(r => <button key={r.value} type="button" role="radio" aria-checked={reason === r.value} onClick={() => setReason(r.value)} className={`rounded-full px-3.5 py-1.5 text-[13px] transition-colors duration-150 ease-soft ${reason === r.value ? 'bg-ink text-shell' : 'bg-sand text-ink hover:bg-blush'}`} data-id="element-346" data-mp="2po98f16yq56j">
            {r.label}
          </button>)}
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-id="element-347" data-mp="38jplz59i12gu">
        <Field label="Datum" data-id="element-348" data-mp="19a0ws1nqdg9o">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className={inputCls} data-id="element-349" data-mp="19b2bqh7vamwo" />
        </Field>
        <Field label="Vanaf" data-id="element-350" data-mp="16ojfp3js75pc">
          <input type="time" step={ADMIN_STEP_MINUTES * 60} value={time} onChange={e => setTime(e.target.value)} className={inputCls} data-id="element-351" data-mp="16ot78vkh5kx5" />
        </Field>
        <Field label="Hoe lang" data-id="element-352" data-mp="11vqfmsz9cljl">
          <select value={durationMinutes} onChange={e => setDuration(Number(e.target.value))} className={inputCls} data-id="element-353" data-mp="wy70lquqk4ql">
            {blockDurations.map(m => <option key={m} value={m} data-id="element-354" data-mp="rxke4chd5hf9">
                {durationLabel(m)}
              </option>)}
          </select>
        </Field>
      </div>

      <Field label="Omschrijving" hint="Optioneel, alleen voor jou. Bijvoorbeeld 'Tandarts' of 'Training emerginC'." data-id="element-355" data-mp="1kpcew1l12oys">
        <input value={label} onChange={e => setLabel(e.target.value)} maxLength={60} className={inputCls} data-id="element-356" data-mp="1fnn1ifac1z7j" />
      </Field>

      {overlapping > 0 && <p role="status" className="rounded-md bg-blush/60 px-3 py-2.5 text-[13px] text-ink" data-id="element-357" data-mp="4akjrgwhz3ls">
          Hier {overlapping === 1 ? 'staat al een afspraak' : `staan al ${plural(overlapping, 'afspraak', 'afspraken')}`}. Die blijft staan; verzet of annuleer hem apart als dat nodig is.
        </p>}

      <div className="flex flex-wrap items-center gap-2" data-id="element-358" data-mp="1swduw4vhlw9j">
        <button type="submit" disabled={!date || !time || submitting} aria-busy={submitting} className={btnPrimary} data-id="element-359" data-mp="1sxyptvlprh7x">
          {submitting ? 'Blokkeren…' : 'Blokkeren'}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-id="element-360" data-mp="1j6c4v7jkq3rz">
          Annuleer
        </button>
      </div>
    </form>;
}